import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { Block, BlockHead, BlockHeadContent, BlockTitle, BlockDes, Button, Icon } from "../../../components/Component";
import { Spinner } from "reactstrap";
import { api, storagePublicUrl } from "../../../utils/api";
import { toast } from "react-toastify";

const HseProcessInspectionDetailPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const baseList = `${process.env.PUBLIC_URL || ""}/hse-process/inspections`;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inspection, setInspection] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getInspection(id);
      setInspection(data);
    } catch (e) {
      toast.error(e.message || "Inspection introuvable");
      history.push(baseList);
    } finally {
      setLoading(false);
    }
  }, [id, history, baseList]);

  useEffect(() => {
    load();
  }, [load]);

  const itemIdMatches = (answer, itemId) =>
    Number(answer.inspection_template_item_id) === Number(itemId);

  const getAnswer = (itemId) => {
    if (!inspection?.answers) return null;
    return inspection.answers.find((a) => itemIdMatches(a, itemId));
  };

  const patchAnswerField = (itemId, field, value) => {
    setInspection((prev) => {
      const answers = [...(prev.answers || [])];
      const idx = answers.findIndex((a) => itemIdMatches(a, itemId));
      const base = {
        inspection_template_item_id: itemId,
        company_id: prev.company_id,
        inspection_id: prev.id,
      };
      if (idx === -1) {
        answers.push({ ...base, [field]: value });
      } else {
        answers[idx] = { ...answers[idx], [field]: value };
      }
      return { ...prev, answers };
    });
  };

  const buildAnswersPayload = () => {
    const items = inspection.template?.items || [];
    return items.map((it) => {
      const a = getAnswer(it.id) || {};
      const row = { inspection_template_item_id: it.id };
      if (it.question_type === "boolean") {
        row.value_boolean = a.value_boolean === null || a.value_boolean === undefined ? null : !!a.value_boolean;
      }
      if (it.question_type === "text") row.value_text = a.value_text ?? null;
      if (it.question_type === "numeric") {
        const n = a.value_numeric;
        row.value_numeric = n === "" || n === null || n === undefined ? null : Number(n);
      }
      if (it.question_type === "rating") {
        const r = a.value_rating;
        row.value_rating = r === "" || r === null || r === undefined ? null : parseInt(r, 10);
      }
      const c = (a.comment ?? "").trim();
      row.comment = c || null;
      return row;
    });
  };

  const recurrenceLabel = (r) =>
    ({ daily: "Quotidien", weekly: "Hebdomadaire", monthly: "Mensuel" }[r] || null);

  const riskFromScore = (pct) => {
    if (pct == null || pct === "") return null;
    const n = Number(pct);
    if (n >= 85) return { label: "Faible", className: "success" };
    if (n >= 60) return { label: "Modéré", className: "info" };
    return { label: "Élevé", className: "danger" };
  };

  const saveDraft = async () => {
    if (!inspection || inspection.status === "submitted") return;
    setSaving(true);
    try {
      await api.updateInspection(inspection.id, {
        status: "in_progress",
        answers: buildAnswersPayload(),
      });
      toast.success("Brouillon enregistré");
      await load();
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const submitFinal = async () => {
    if (!inspection || inspection.status === "submitted") return;
    setSaving(true);
    try {
      await api.updateInspection(inspection.id, {
        answers: buildAnswersPayload(),
      });
      await api.submitInspection(inspection.id);
      toast.success("Inspection soumise");
      await load();
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur soumission");
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (itemId, file) => {
    if (!file || !inspection) return;
    const ans = getAnswer(itemId);
    if (!ans?.id) {
      await api.updateInspection(inspection.id, {
        answers: [{ inspection_template_item_id: itemId }],
      });
      await load();
    }
    const fresh = await api.getInspection(inspection.id);
    const a2 = fresh.answers?.find((x) => itemIdMatches(x, itemId));
    if (!a2?.id) {
      toast.error("Impossible d’associer la photo");
      return;
    }
    try {
      await api.uploadInspectionAnswerPhoto(inspection.id, a2.id, file);
      toast.success("Photo envoyée");
      await load();
    } catch (e) {
      toast.error(e.message || "Erreur envoi photo");
    }
  };

  if (loading || !inspection) {
    return (
      <Content>
        <div className="text-center py-5">
          <Spinner />
        </div>
      </Content>
    );
  }

  const readOnly = inspection.status === "submitted";
  const items = inspection.template?.items || [];
  const assignee = inspection.assigned_to || inspection.assignedTo;
  const rec = inspection.template?.recurrence;
  const genObs = inspection.generated_observations || inspection.generatedObservations || [];
  const srcInc = inspection.source_incidents || inspection.sourceIncidents || [];
  const obsBase = `${process.env.PUBLIC_URL || ""}/observations`;
  const incBase = `${process.env.PUBLIC_URL || ""}/hse-process/incidents`;
  const risk = riskFromScore(inspection.score?.score_percent);

  return (
    <React.Fragment>
      <Head title={`Inspection #${inspection.id}`} />
      <Content>
        <BlockHead size="sm">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <BlockHeadContent>
              <BlockTitle page>Inspection #{inspection.id}</BlockTitle>
              <BlockDes className="text-soft">
                {inspection.template?.name} — {inspection.site?.name}
                {assignee && (
                  <>
                    {" "}
                    — Assigné : {assignee.first_name} {assignee.last_name}
                  </>
                )}
                {recurrenceLabel(rec) && <> — Fréquence modèle : {recurrenceLabel(rec)}</>}
                {readOnly && " — Soumise"}
              </BlockDes>
              <p className="text-soft small mt-2 mb-0" style={{ maxWidth: "42rem" }}>
                Il s&apos;agit d&apos;une occurrence terrain du modèle : vous complétez ici la checklist (réponses par
                question), éventuellement des commentaires ou des photos, puis vous enregistrez un brouillon ou vous
                soumettez. Après soumission, la fiche devient en lecture seule ; le score, les observations et les alertes
                HSE éventuelles sont gérées automatiquement côté serveur.
              </p>
            </BlockHeadContent>
            <div>
              <Button color="light" className="me-2" onClick={() => history.push(baseList)}>
                <Icon name="arrow-left" />
                <span>Retour</span>
              </Button>
              {!readOnly && (
                <>
                  <Button color="light" className="me-2" onClick={saveDraft} disabled={saving}>
                    Enregistrer
                  </Button>
                  <Button color="primary" onClick={submitFinal} disabled={saving}>
                    Soumettre
                  </Button>
                </>
              )}
            </div>
          </div>
        </BlockHead>

        <Block>
          {items.length === 0 ? (
            <p className="text-soft">Aucune question sur ce modèle.</p>
          ) : (
            items.map((it) => {
              const a = getAnswer(it.id) || {};
              return (
                <div key={it.id} className="card card-bordered mb-3">
                  <div className="card-inner">
                    <h6 className="title mb-1">
                      {it.label}
                      {it.required && <span className="text-danger"> *</span>}
                    </h6>
                    {it.help_text && <p className="text-soft small">{it.help_text}</p>}
                    {it.question_type === "boolean" && (
                      <select
                        className="form-select"
                        disabled={readOnly}
                        value={a.value_boolean === true ? "1" : a.value_boolean === false ? "0" : ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          patchAnswerField(it.id, "value_boolean", v === "" ? null : v === "1");
                        }}
                      >
                        <option value="">—</option>
                        <option value="1">Oui</option>
                        <option value="0">Non</option>
                      </select>
                    )}
                    {it.question_type === "text" && (
                      <textarea
                        className="form-control"
                        rows={3}
                        disabled={readOnly}
                        value={a.value_text || ""}
                        onChange={(e) => patchAnswerField(it.id, "value_text", e.target.value)}
                      />
                    )}
                    {it.question_type === "numeric" && (
                      <input
                        type="number"
                        step="any"
                        className="form-control"
                        disabled={readOnly}
                        value={a.value_numeric ?? ""}
                        onChange={(e) => patchAnswerField(it.id, "value_numeric", e.target.value === "" ? null : e.target.value)}
                      />
                    )}
                    {it.question_type === "rating" && (
                      <input
                        type="number"
                        min={0}
                        max={10}
                        className="form-control"
                        disabled={readOnly}
                        value={a.value_rating ?? ""}
                        onChange={(e) => patchAnswerField(it.id, "value_rating", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                      />
                    )}
                    {it.question_type === "photo" && (
                      <div>
                        {a.photo_path && (
                          <div className="mb-2">
                            <img src={storagePublicUrl(a.photo_path)} alt="" className="img-thumbnail" style={{ maxHeight: 160 }} />
                          </div>
                        )}
                        {!readOnly && <input type="file" accept="image/*" className="form-control" onChange={(e) => onPhoto(it.id, e.target.files?.[0])} />}
                      </div>
                    )}
                    <label className="form-label mt-2">Commentaire terrain</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      placeholder="Contexte, précision…"
                      disabled={readOnly}
                      value={a.comment ?? ""}
                      onChange={(e) => patchAnswerField(it.id, "comment", e.target.value)}
                    />
                  </div>
                </div>
              );
            })
          )}

          {inspection.score && (
            <div className={`alert alert-pro alert-${risk?.className || "success"}`}>
              <strong>Conformité :</strong> {inspection.score.score_percent}% ({inspection.score.earned_points} /{" "}
              {inspection.score.total_points})
              {risk && (
                <>
                  {" "}
                  — <strong>Niveau de risque (indicatif) :</strong> {risk.label}
                </>
              )}
            </div>
          )}

          {readOnly && Array.isArray(inspection.score?.breakdown) && inspection.score.breakdown.length > 0 && (
            <div className="card card-bordered mb-3">
              <div className="card-inner">
                <h6 className="title">Détail par question</h6>
                <ul className="list list-sm mb-0">
                  {inspection.score.breakdown.map((row) => (
                    <li key={row.item_id}>
                      <span className={`badge text-bg-${row.compliant ? "success" : "danger"} me-2`}>
                        {row.compliant ? "OK" : "NOK"}
                      </span>
                      {row.label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {readOnly && genObs.length > 0 && (
            <div className="alert alert-warning">
              <strong>Observations générées automatiquement</strong> (non-conformités) :
              <ul className="mb-0 mt-2">
                {genObs.map((o) => (
                  <li key={o.id}>
                    <a href={`${obsBase}/${o.id}/view`} className="link">
                      #{o.id} — {o.title} ({o.status})
                    </a>
                  </li>
                ))}
              </ul>
              {inspection.template?.create_incident_on_non_compliance && (
                <p className="small mb-0 mt-2">
                  Ce modèle prévoit aussi la création d’<strong>incidents</strong> (un par NOK) : consultez la liste{" "}
                  <a href={`${process.env.PUBLIC_URL || ""}/hse-process/incidents`} className="link">
                    Processus HSE → Incidents
                  </a>
                  .
                </p>
              )}
            </div>
          )}

          {readOnly && srcInc.length > 0 && (
            <div className="alert alert-danger">
              <strong>Incidents générés</strong> (option modèle NOK) :
              <ul className="mb-0 mt-2">
                {srcInc.map((inc) => (
                  <li key={inc.id}>
                    <a href={`${incBase}`} className="link">
                      #{inc.id} — {inc.title} ({inc.status})
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default HseProcessInspectionDetailPage;
